import React, { useMemo } from "react";
import { useParams } from "react-router-dom";
import { StickyNote } from "lucide-react";
import { useIonRouter } from "@/app/IonicContextProvider";
import { useShifts } from "../hooks";
import { ShiftStatusSection } from "../components/ShiftStatusSection";
import { TimeDetailCards } from "../components/TimeDetailCards";
import { LocationCheckCard } from "../components/LocationCheckCard";
import { formatDateInZone } from "../utils/formatShiftTime";

export function ShiftDetailsPage() {
  const router = useIonRouter();
  const { shiftId } = useParams<{ shiftId: string }>();

  const goToShifts = () => router.replace("/shifts");
  const { data: shifts, isLoading } = useShifts();

  const shift = useMemo(() => {
    if (!shifts || !shiftId) return null;
    return shifts.find((s) => s.id === parseInt(shiftId, 10));
  }, [shifts, shiftId]);

  // Format date in shift's timezone
  const shiftDate = useMemo(() => {
    if (!shift?.started_at) return "";
    return formatDateInZone(shift.started_at, shift.timezone);
  }, [shift?.started_at, shift?.timezone]);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-muted-foreground">Loading shift details...</div>
      </div>
    );
  }

  if (!shift) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4">
        <p className="text-muted-foreground mb-4">Shift not found</p>
        <button
          type="button"
          onClick={goToShifts}
          className="text-primary hover:underline"
        >
          Back to Shifts
        </button>
      </div>
    );
  }

  if (!shift.ended_at) {
    return (
      <div className="flex flex-col items-center justify-center h-full p-4">
        <p className="text-muted-foreground mb-4">This shift is still in progress</p>
        <button
          type="button"
          onClick={goToShifts}
          className="text-primary hover:underline"
        >
          Back to Shifts
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full bg-gray-50">
      <div className="flex-1 overflow-y-auto">
        {/* Shift Status Section - Green "Shift Completed" */}
        <ShiftStatusSection shiftDate={shiftDate} />

        {/* Time Detail Cards - Clock In / Clock Out */}
        <TimeDetailCards shift={shift} />

        {/* Shift Notes Section */}
        {shift.notes && (
          <section className="bg-white rounded-xl m-4 p-4 border-gray-200 border">
            <div className="flex items-center gap-2 mb-3">
              <div className="h-5 w-5 rounded flex items-center justify-center">
                <StickyNote className="text-primary" />
              </div>
              <h2 className="text-sm font-semibold text-gray-700 my-1 flex items-center gap-2 m-0">Shift Notes</h2>
            </div>
            <p className="text-sm text-gray-800 whitespace-pre-wrap">{shift.notes}</p>
          </section>
        )}

        {/* Location Check Card - below notes, shows clock-in location (lat/long) when available */}
        {shift.start_latitude != null && shift.start_longitude != null && (
          <LocationCheckCard
            coords={{
              latitude: shift.start_latitude,
              longitude: shift.start_longitude,
            }}
            address={null}
            loading={false}
          />
        )}
      </div>

      {/* Action Button */}
      <section className="px-4 pb-6 space-y-3 bg-white border-t border-gray-200 pt-4">
        <button
          type="button"
          onClick={goToShifts}
          className="w-full text-center text-gray-700 font-medium py-2 hover:text-gray-900 transition-colors"
        >
          Back to Shifts
        </button>
      </section>
    </div>
  );
}

